import { useState, useEffect } from 'react';
import api from '../services/api';
import { UNIT_SYSTEMS } from '../utils/unitConverter';

/**
 * Custom hook to load and manage unit preferences
 * @returns {Object} Unit preferences, loading state, and utility functions
 */
const useUnitPreferences = () => {
  const [unitPreferences, setUnitPreferences] = useState({
    preferred_unit_system: UNIT_SYSTEMS.INTERNATIONAL
  });
  const [loading, setLoading] = useState(true);

  // Listen for unit preference updates from SystemSettingsModal
  const handleUnitPreferencesUpdate = (event) => {
    console.log("Received unit preferences update:", event.detail);
    setUnitPreferences(event.detail);
    
    // Also update localStorage for immediate access
    const currentPrefs = JSON.parse(localStorage.getItem('dashboard_preferences') || '{}');
    const updatedPrefs = { ...currentPrefs, ...event.detail };
    localStorage.setItem('dashboard_preferences', JSON.stringify(updatedPrefs));
  };

  useEffect(() => {
    // Add event listener for unit preferences updates
    window.addEventListener('unitPreferencesUpdated', handleUnitPreferencesUpdate);

    // Load unit preferences
    const loadUnitPreferences = async () => {
      try {
        // Try to load from API first
        const response = await api.get('/dashboard/dashboard-preferences');
        console.log("Loaded unit preferences from API:", response.data);
        const prefs = response.data || {};
        setUnitPreferences({
          preferred_unit_system: prefs.preferred_unit_system || UNIT_SYSTEMS.INTERNATIONAL
        });
      } catch (error) {
        console.error('Error loading unit preferences:', error);

        // Try to load from localStorage as fallback
        try {
          const storedPrefs = localStorage.getItem('dashboard_preferences');
          if (storedPrefs) {
            const parsedPrefs = JSON.parse(storedPrefs);
            console.log("Loaded unit preferences from localStorage:", parsedPrefs);
            setUnitPreferences({
              preferred_unit_system: parsedPrefs.preferred_unit_system || UNIT_SYSTEMS.INTERNATIONAL
            });
          }
        } catch (e) {
          console.error('Error parsing stored unit preferences:', e);
          // Keep defaults if localStorage parse fails
        }
      } finally {
        setLoading(false);
      }
    };

    loadUnitPreferences();

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('unitPreferencesUpdated', handleUnitPreferencesUpdate);
    };
  }, []);

  /**
   * Get the user's preferred unit system
   * @returns {string} The preferred unit system
   */
  const getPreferredUnitSystem = () => {
    return unitPreferences.preferred_unit_system || UNIT_SYSTEMS.INTERNATIONAL;
  };

  /**
   * Check if user prefers international (SI) units
   * @returns {boolean} True if international units preferred
   */
  const isInternationalSystem = () => {
    return getPreferredUnitSystem() === UNIT_SYSTEMS.INTERNATIONAL;
  };

  /**
   * Check if user prefers imperial units
   * @returns {boolean} True if imperial units preferred
   */
  const isImperialSystem = () => {
    return getPreferredUnitSystem() === UNIT_SYSTEMS.IMPERIAL;
  };

  /**
   * Get display unit for a measurement type based on user preferences
   * @param {string} measurementType - Type of measurement (weight, height, etc.)
   * @returns {string} The appropriate display unit
   */
  const getDisplayUnit = (measurementType) => {
    const unitSystem = getPreferredUnitSystem();
    const { UNITS } = require('../utils/unitConverter');
    return UNITS[measurementType.toUpperCase()]?.[unitSystem] || 'N/A';
  };

  return { 
    unitPreferences, 
    loading,
    getPreferredUnitSystem,
    isInternationalSystem,
    isImperialSystem,
    getDisplayUnit
  };
};

export default useUnitPreferences;